import { Grid2, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Attachments from '@components/Attachments';
import CollapsibleCard from '@components/CollapsibleCard';
import { CurationStatusBadge } from '@components/CurationStatusBadge';
import Icon from '@components/Icon/Icon';
import { RecordSourcePill } from '@components/RecordSourcePill';
import SelfReportedPill from '@components/SelfReportedPill';

import { IMMUNIZATIONS } from '@constants/routes';

import { useGetImmunization } from '@hooks/api/immunization';
import { useSession } from '@hooks/useSession';

import type { CurationStatus, ResourceSourceType } from 'generated/types';

const messages = defineMessages({
  lot: {
    id: 'immunizationCard.lot',
    defaultMessage: 'Lot',
  },
  dose: {
    id: 'immunizationCard.dose',
    defaultMessage: 'Dose',
  },
  moreInfo: {
    id: 'immunizationCard.moreInfo',
    defaultMessage: 'More information',
  },
  lessInfo: {
    id: 'immunizationCard.lessInfo',
    defaultMessage: 'Less information',
  },
  immunizationRequired: {
    id: 'immunizationCard.immunizationRequired',
    defaultMessage: 'Immunization Required',
  },
  due: {
    id: 'immunizationCard.due',
    defaultMessage: 'Due',
  },
  pastDue: {
    id: 'immunizationCard.pastDue',
    defaultMessage: 'Past Due',
  },
  unknownDate: {
    id: 'immunizationCard.unknownDate',
    defaultMessage: 'Unknown Date',
  },
  unknown: {
    id: 'GLOBAL.unknown',
    defaultMessage: 'Unknown',
  },
  brand: {
    id: 'immunizationCard.brand',
    defaultMessage: 'Brand',
  },
  selfReported: {
    id: 'GLOBAL.selfReported',
    defaultMessage: 'Self-Reported',
  },
});

interface ImmunizationCardProps {
  patient?: string | null;
  type?: string | null;
  location?: string | null;
  dateReceived?: string | null;
  dateExpires?: string | null;
  lot?: string | null;
  brand?: string | null;
  dose?: string | null;
  hideExpiry?: boolean | null;
  provider?: string | null;
  selfReported?: boolean | null;
  id?: string | null;
  status?: CurationStatus;
  recordSource?: ResourceSourceType;
}

// Fetches attachments only when the card is expanded. CollapsibleCard uses
// <Collapse unmountOnExit>, so this component only mounts on expand and the
// query is skipped otherwise.
function ImmunizationAttachmentsSection({
  id,
  patient,
}: {
  id?: string | null;
  patient?: string | null;
}) {
  const { data } = useGetImmunization({ record_id: id ?? '' });
  if (!id) return null;
  return (
    <Attachments
      patient_id={patient}
      attachments={data?.attachments}
      resource={`Immunization/${id}`}
    />
  );
}

function ImmunizationCard({
  patient,
  type = '',
  location = '',
  dateReceived,
  dateExpires,
  id = '',
  lot = '',
  brand = '',
  dose = '',
  hideExpiry = false,
  provider = '',
  selfReported = false,
  status,
  recordSource,
}: ImmunizationCardProps) {
  const { formatMessage } = useIntl();
  const {
    state: { isCurator, isPractitioner, isVitall },
  } = useSession();

  const daysTillExpiry = dayjs(dateExpires ?? 0).diff(new Date(), 'days');

  function getExpirationColor(difference: number) {
    if (difference < 0) return 'error.main';
    else if (difference < 90) return 'warning.main';
    return 'success.main';
  }

  const handleClick = () => {
    if (isCurator) {
      navigate(`${IMMUNIZATIONS}${id}`);
    }
  };

  return (
    <CollapsibleCard
      onCardClick={isCurator ? handleClick : undefined}
      cardContent={
        <Grid2 container justifyContent="space-between">
          <Grid2 size={{ xs: 6 }}>
            <Typography
              color="text.secondary"
              sx={{ fontWeight: 'light' }}
              variant="body2"
            >
              {dateReceived ?? formatMessage(messages.unknownDate)}
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 6 }} textAlign="end">
            <Typography
              color={getExpirationColor(daysTillExpiry)}
              sx={{ fontWeight: 'light' }}
              variant="body2"
            >
              {dateExpires && !hideExpiry
                ? `${
                    daysTillExpiry < 0
                      ? formatMessage(messages.pastDue)
                      : formatMessage(messages.due)
                  }: ${dateExpires}`
                : null}
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: isVitall || isPractitioner ? 7 : 12 }}>
            <Typography variant="body1">{type}</Typography>
          </Grid2>
          {isVitall ? (
            <Grid2 textAlign="end" size={{ xs: 'auto' }}>
              <CurationStatusBadge status={status} />
            </Grid2>
          ) : null}
          {isVitall || isPractitioner ? (
            <Grid2 textAlign="end" size={{ xs: 'auto' }}>
              <RecordSourcePill type={recordSource} />
            </Grid2>
          ) : null}
        </Grid2>
      }
      expandedContent={
        <>
          <Grid2 container justifyContent="space-between">
            <Grid2 size={{ xs: 8 }}>
              {location ? (
                <Stack direction="row" spacing={1}>
                  <Icon
                    style={{ fontSize: 20 }}
                    color="warning.main"
                    name="location-dot"
                    type="fas"
                  />
                  <Typography variant="body2">{location}</Typography>
                </Stack>
              ) : null}
              {provider ? (
                <Stack direction="row" spacing={1}>
                  <Icon
                    style={{ fontSize: 20 }}
                    color="warning.main"
                    name="user-doctor"
                    type="fas"
                  />
                  <Typography variant="body2">{provider}</Typography>
                </Stack>
              ) : null}
            </Grid2>
            <Grid2 textAlign="end" size={{ xs: 4 }}>
              <Typography
                color="text.secondary"
                variant="body2"
              >{`${formatMessage(messages.lot)} # ${
                lot ? lot : formatMessage(messages.unknown)
              }`}</Typography>
              <Typography
                color="text.secondary"
                variant="body2"
              >{`${formatMessage(messages.dose)}: ${
                dose ? dose : formatMessage(messages.unknown)
              }`}</Typography>
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
              <Typography
                color="text.secondary"
                variant="body2"
              >{`${formatMessage(messages.brand)}: ${
                brand ? brand : formatMessage(messages.unknown)
              }`}</Typography>
            </Grid2>
            <Grid2 size={{ xs: 8 }}>
              <ImmunizationAttachmentsSection id={id} patient={patient} />
            </Grid2>
            <Grid2 textAlign="end" size={{ xs: 4 }}>
              <SelfReportedPill value={selfReported ?? false} />
            </Grid2>
          </Grid2>
        </>
      }
    />
  );
}

export default ImmunizationCard;
